import { Button } from '@components/buttons'
import { OrderToolbar } from '@components/tables/Toolbars/MedicalOrderToolbar'
import { Text } from '@components/texts'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import DifferenceRoundedIcon from '@mui/icons-material/DifferenceRounded'
import ErrorIcon from '@mui/icons-material/Error'
import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import MergeIcon from '@mui/icons-material/Merge'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  LinearProgress,
  Popover,
  Tooltip,
  Typography,
  Button as MuiButton,
} from '@mui/material'
// Menu handled in QuickFseActions component
import cartevital from '@assets/img/cartevital.png'
import balanceIcon from '@assets/svgs/balance.svg'
import { useCommentsPopover } from '@hooks/useCommentPopover'
import { useIframeMessages } from '@hooks/useIframeMessages'
import { useWorklistUrlState } from '@hooks/useUrlState'
import PaymentIcon from '@mui/icons-material/Payment'
import {
  DataGridPro,
  GridColumns,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro'
import {
  BillStatus,
  OrderDto,
  PatientAldDTO,
  TransactionType,
} from '@services/dtos'
import {
  findPatientsDebts,
  getOrdersWithUnpaidInvoices,
} from '@state/reducers/invoiceReducer'
import {
  doFindOrders,
  doGetOrder,
  doGetOrderDocuments,
  doUpdateOrder,
  setOrderFilters,
  setSelectedOrder,
} from '@state/reducers/orderReducer'
import theme from '@utils/theme'
import { capitalize } from 'lodash'
import {
  CarTaxiFront,
  Files,
  FileText,
  Folder,
  HistoryIcon,
  MessageCircle,
  ShieldCheck,
} from 'lucide-react'
import moment from 'moment'
import 'moment/locale/fr'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { OrderStatusEnum } from '../../common/interfaces'
import { TransportForm } from '../../containers/app/ambulance/TransportForm'
import RegisterPatientForm from '../../containers/app/billing/form/RegisterPatientForm'
import PatientFusionDialog from '../../containers/app/Worklists/DialogPatientFusion'
import OrderDialog, {
  SlideInPortal,
} from '../../containers/app/Worklists/MedicalOrderDialog'
import BillStatusDialog from '../../containers/app/Worklists/Quotation/BillStatusDialog'
import { Document, PatientTransportStatus } from '../../services/api'
import { doGetOrderTransports } from '../../state/reducers/ambulanceReducer'
import { setPatientDetails } from '../../state/reducers/patientsReducer'
import { UpTransition } from '../animations'
import InstantBillComponent from '../billing/InstantBillComponent'
import QuickFseActions from '../billing/QuickFseActions'
import UnpaidPatientInvoices from '../billing/UnpaidPatientInvoices'
import PatientPaymentForm from '../forms/PatientPaymentForm'
import PatientAldList from '../PatientAldDetails'
import ReportSelection from '../reports/ReportSelection'
import { DialogTitle } from '../titles'
import {
  CommentsPopover,
  CommentsPopoverTrigger,
} from './components/CommentsPopover'

import { useReportWindow } from '@hooks/useReportWindow'
import DocumentsList from '../documents/DocumentsList'
import { updateOrderDocuments } from '@state/reducers/orderReducer'
import DocumentListViewer from '../dialogs/DocumentListViewer'
import { EntityNameEnum } from '@utils/enums'
import EventLogsOrderTable from './EventLogsOrderTable'
import UnpaidPatientInvoicesDialog from '../dialogs/UnpaidPatientInvoicesDialog'

export interface DialogProps {
  openPatientDetails: boolean
}

const actionsMap: { [key: string]: string[] } = {
  [OrderStatusEnum.pendingRegistration]: [
    'registerPatient',
    'registerPatientWithCV',
    'updateMO',
    'patientDetails',
    'patientMerge',
    'ViewMo',
    'aldStatus',
    'transport',
    // 'Dmp',
    'validerFse',
    'quickPay',
    'unpaidInvoices',
    'documents',
    'eventLogs',
  ],
  [OrderStatusEnum.registered]: [
    'registerPatient',
    'ViewMo',
    'updateMO',
    'patientDetails',
    'patientMerge',
    'transport',
    'validerFse',
    'quickPay',
    'unpaidInvoices',
    // 'Dmp',
    'documents',
    'eventLogs',
  ],
  [OrderStatusEnum.toQuote]: [
    'registerPatient',
    'ViewMo',
    'patientFusion',
    'transport',
    'validerFse',
    'quickPay',
    'unpaidInvoices',
    'documents',
    'eventLogs',
  ],
  [OrderStatusEnum.toInvoice]: [
    'registerPatient',
    'ViewMo',
    'patientBill',
    'patientFusion',
    'validerFse',
    'quickPay',
    'transport',
    'unpaidInvoices',
    // 'Dmp',
    'documents',
    'eventLogs',
  ],
  [OrderStatusEnum.PAID]: [
    'registerPatient',
    'ViewMo',
    'transport',
    'unpaidInvoices',
    'documents',
    'eventLogs',
  ],
  [OrderStatusEnum.PARTIALLY_PAID]: [
    'registerPatient',
    'ViewMo',
    'transport',
    'unpaidInvoices',
    'documents',
    'eventLogs',
  ],
  [OrderStatusEnum.medicalOrderCancelled]: [
    'registerPatient',
    'ViewMo',
    'transport',
    'unpaidInvoices',
    'documents',
    'eventLogs',
  ],
  [OrderStatusEnum.toCreateReport]: [
    'patientMerge',
    'patientDetails',
    'ViewMo',
    'transport',
    'unpaidInvoices',
    'report',
    'validerFse',
    'documents',
    'eventLogs',
  ],
}

type Props = {
  isSelection?: boolean
  onSelectionChange?: (selectedRows: OrderDto[]) => void
  selectionModel?: number[]
  excludeRows?: number[]
}

export default function OrdersTable({
  isSelection,
  onSelectionChange,
  excludeRows,
}: Props) {
  const dispatch = useAppDispatch()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const [isInstantBillOpen, setIsInstantBillOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [unpaidOpen, setUnpaidOpen] = useState(false)
  const [unpaidPatientId, setUnpaidPatientId] = useState<number | null>(null)
  const [unpaidPatientName, setUnpaidPatientName] = useState<string>('')
  const [validerAnchorEl, setValiderAnchorEl] = useState<HTMLElement | null>(
    null,
  )
  const [validerOrder, setValiderOrder] = useState<OrderDto | null>(null)
  const [paymentInvoice, setPaymentInvoice] = useState<any | null>(null)
  const [quickPayPatientId, setQuickPayPatientId] = useState<number | null>(
    null,
  )
  const [quickPayOpen, setQuickPayOpen] = useState(false)
  const [selectedReport, setSelectedReport] = useState<string | null>(null)

  const [showBillStatusDialog, setShowBillStatusDialog] = useState(false)
  const [billStatusOrderId, setBillStatusOrderId] = useState<number | null>(
    null,
  )
  const [isEditingDocuments, setisEditingDocuments] = useState(false)
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false)
  const [initialOrderIdHandled, setInitialOrderIdHandled] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null,
  )
  const [isEventLogsOpen, setIsEventLogsOpen] = useState(false)

  const { urlState, updateParam, updateParams, clearParams } =
    useWorklistUrlState()

  useEffect(() => {
    if (!urlState.startDate && !urlState.endDate && !urlState.patientIds) {
      const todayStart = moment().startOf('day').valueOf()
      const todayEnd = moment().endOf('day').valueOf()
      updateParams({ startDate: todayStart, endDate: todayEnd })
    }
  }, [])

  const { registerIframe } = useIframeMessages()

  const [dialogAction, setDialogAction] = useState<
    | 'PatientDetails'
    | 'PatientForm'
    | 'patientFusion'
    | 'MedicalOrder'
    | 'ALDStatus'
    | 'Couverture'
    | 'Transport'
    | 'Report'
    | 'Dmp'
    | 'Documents'
    | 'eventLogs'
    | null
  >(null)

  const {
    selectedOrder,
    filters,
    orders,
    patientsDebts,
    invoicesListByOrders,
    user,
  } = useAppSelector((state) => ({
    user: state.auth.user,
    medicalOrders: state.order.orders,
    filters: state.order.filters,
    selectedOrder: state.order.selectedOrder,
    orders: state.order.orders,
    patientsDebts: state.invoice.patientsDebts,
    invoicesListByOrders: state.invoice.invoicesListByOrders,
  }))

  const {
    commentsPopover,
    commentDialogOpen,
    editingComment,
    handleOpenComments,
    handleCloseComments,
    setCommentDialogOpen,
    setEditingComment,
  } = useCommentsPopover()

  const unpaidCountByPatientId = useMemo(() => {
    const map = new Map<number, number>()
    patientsDebts?.forEach((d) => {
      if (d.totalDebt > 0) {
        map.set(d.patientId, d.numberOfInvoices || 1)
      }
    })
    return map
  }, [patientsDebts])

  const orderToInvoiceMap = useMemo(() => {
    const map = new Map<number, any>()
    invoicesListByOrders?.forEach((invoice) => {
      if (invoice.orderId) {
        map.set(invoice.orderId, invoice)
      }
    })
    return map
  }, [invoicesListByOrders])

  const CustomALDIcon = ({ aldStatus }: { aldStatus: PatientAldDTO[] }) => {
    const activeAld = aldStatus.find(
      (ald) =>
        ald.etat === 'actif' &&
        (ald.dateFin === null || new Date(ald.dateFin || '') > new Date()),
    )
    if (activeAld && activeAld.code === '30') {
      return (
        <Tooltip title={`ALD: ${activeAld.libelle} (${activeAld.etat})`}>
          <HealthAndSafetyRoundedIcon className="text-green-500" />
        </Tooltip>
      )
    }

    const aldWithComment = aldStatus.find((ald) => ald.comment)
    if (aldWithComment) {
      return (
        <Tooltip title={aldWithComment.comment}>
          <HealthAndSafetyRoundedIcon className="text-orange-500" />
        </Tooltip>
      )
    } else {
      return (
        <Tooltip title="Informations ALD manquantes">
          <HealthAndSafetyRoundedIcon className="text-red-500" />
        </Tooltip>
      )
    }
  }

  const CustomCouvertureIcon: React.FC<{ coverageId?: number }> = ({
    coverageId,
  }) => {
    if (coverageId) {
      return <DifferenceRoundedIcon className="text-green-500" />
    } else {
      return (
        <Tooltip title="Couverture non mise à jour">
          <DifferenceRoundedIcon className="text-orange-500" />
        </Tooltip>
      )
    }
  }

  const actionBtnObj = useMemo(
    () => [
      {
        id: 1,
        btnName: 'Accueillir le patient',
        icon: <MeetingRoomIcon className="text-blue-500" />,
        action: (row: OrderDto) => handleRegisterPatientClick(row),
        actionKey: 'registerPatient',
      },
      {
        id: 2,
        btnName: 'Fusionner le patient',
        icon: <MergeIcon className="text-purple-500" />,
        action: (row: OrderDto) => {
          handleClickActionPatientMerge()
          dispatch(setSelectedOrder(row))
        },
        actionKey: 'patientMerge',
      },
      {
        id: 3,
        btnName: 'Voir facture',
        icon: <ErrorIcon className="text-green-500" />,
        action: (row: OrderDto) => handleClickActionPatientBill(row),
        actionKey: 'patientBill',
      },
      {
        id: 4,
        btnName: 'Fiche patient',
        icon: <AssignmentIndIcon className="text-teal-500" />,
        action: (row: OrderDto) => {
          handleCouvertureClick(row)
        },
        actionKey: 'patientDetails',
      },
      {
        id: 5,
        btnName: 'Voir',
        actionKey: 'ViewMo',
        icon: <VisibilityIcon className="text-slate-500" />,
        action: (row: OrderDto) => {
          handleOrderClick(row)
        },
      },
      {
        id: 6,
        btnName: 'Transport',
        actionKey: 'transport',
        icon: <CarTaxiFront className="text-orange-500 text-sm" />,
        action: (row: OrderDto) => {
          handleTransportClick(row)
        },
      },
      {
        id: 7,
        btnName: 'Compte rendu',
        actionKey: 'report',
        icon: <FileText className="text-slate-500" />,
        action: () => {},
      },
      {
        id: 8,
        btnName: 'DMP',
        actionKey: 'Dmp',
        icon: (
          <div className="flex flex-col items-start relative">
            <Folder className="w-[24px] " />
            <span className="text-[8px] absolute top-2 left-1">DMP</span>
          </div>
        ),
        action: (row: OrderDto, event: React.MouseEvent<HTMLDivElement>) => {
          handleDmpClick(row, event)
        },
      },
      {
        id: 9,
        btnName: 'Valider fse',
        actionKey: 'validerFse',
        icon: (
          <img
            src={cartevital}
            alt="carte vitale"
            className="w-[26px] duration-300 group-hover:scale-110 group-hover:rotate-6 translate-y-1"
          />
        ),
        action: (row: OrderDto, event: React.MouseEvent<HTMLDivElement>) => {
          dispatch(setSelectedOrder(row))
          setValiderOrder(row)
          setValiderAnchorEl(event.currentTarget as HTMLElement)
        },
      },
      {
        id: 10,
        btnName: 'Factures impayées',
        actionKey: 'unpaidInvoices',
        icon: (
          <div className="relative">
            <img src={balanceIcon} alt="Balance" className="w-6 h-6" />
          </div>
        ),
        action: (row: OrderDto) => {
          setQuickPayPatientId(row.patient.id)
          setQuickPayOpen(true)
        },
      },
      {
        id: 11,
        btnName: 'Payer une facture',
        actionKey: 'quickPay',
        icon: <PaymentIcon className="text-emerald-700" />,
        action: (row: OrderDto) => {
          const invoice = orderToInvoiceMap.get(row.id)
          if (invoice) {
            setPaymentInvoice(invoice)
          } else {
            dispatch(
              enqueueSnackbar({
                message: 'Aucune facture trouvée pour cette demande',
                options: { variant: 'warning' },
              }),
            )
          }
        },
      },
      {
        id: 12,
        btnName: 'Documents',
        actionKey: 'documents',
        icon: (
          <IconButton size="small" className="relative">
            <Files size={20} />
          </IconButton>
        ),
        action: (row: OrderDto) => {
          handleDocumentsClick(row)
        },
      },
      {
        id: 13,
        btnName: 'Event Logs',
        actionKey: 'eventLogs',
        icon: (
          <IconButton size="small" className="relative">
            <HistoryIcon size={20} />
          </IconButton>
        ),
        action: (row: OrderDto) => {
          handleEventLogsClick(row)
        },
      },
    ],
    [orderToInvoiceMap, dispatch],
  )

  useEffect(() => {
    const isRadiologyState = urlState.state === 'radiologyStudy'
    dispatch(
      setOrderFilters({
        startDate: urlState.startDate ?? undefined,
        endDate: urlState.endDate ?? undefined,
        search: urlState.search ?? undefined,
        siteId: urlState.siteId ?? undefined,
        status: isRadiologyState
          ? undefined
          : (urlState.state as OrderStatusEnum) ||
            OrderStatusEnum.pendingRegistration,
        hasRadiologyStudy: isRadiologyState ? true : undefined,
        patientIds: urlState.patientIds ?? undefined,
      }),
    )
  }, [
    urlState.startDate,
    urlState.endDate,
    urlState.search,
    urlState.siteId,
    urlState.state,
    urlState.patientIds,
    dispatch,
  ])

  useEffect(() => {
    if (!urlState?.orderId || isLoading) return
    if (initialOrderIdHandled) return
    dispatch(doGetOrder(urlState.orderId!))
      .unwrap()
      .then(() => {
        setDialogAction('MedicalOrder')
        setInitialOrderIdHandled(true)
      })
      .catch(() => {
        enqueueSnackbar('Demande non trouvée', { variant: 'warning' })
        updateParam('orderId', null)
      })
  }, [urlState?.orderId, isLoading, updateParam])

  useEffect(() => {
    if (!urlState?.orderId) {
      setInitialOrderIdHandled(false)
    }
  }, [urlState?.orderId])

  const handleALDStatusClick = (order: OrderDto) => {
    dispatch(setSelectedOrder(order))
    setDialogAction('ALDStatus')
  }

  const handleRegisterPatientClick = (order: OrderDto) => {
    dispatch(setSelectedOrder(order))
    setDialogAction('PatientForm')
  }
  const handleClickActionPatientMerge = () => {
    setDialogAction('patientFusion')
  }

  const handleMedicalOrderView = () => {
    setDialogAction('MedicalOrder')
  }

  const handleReportClick = (
    order: OrderDto,
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    setAnchorEl(event.currentTarget)
    setDialogAction('Report')
    dispatch(setSelectedOrder(order))
  }

  const handleClickActionPatientBill = (mo: OrderDto) => {
    console.log(mo)
  }

  const handleCouvertureClick = (order: OrderDto) => {
    dispatch(setSelectedOrder(order))
    setDialogAction('Couverture')
  }

  const handleTransportClick = (order: OrderDto) => {
    dispatch(setSelectedOrder(order))
    setDialogAction('Transport')
  }

  const handlePaymentDialogClose = () => {
    if (paymentInvoice?.orderId) {
      setBillStatusOrderId(paymentInvoice.orderId)
      setShowBillStatusDialog(true)
    }
    setPaymentInvoice(null)
  }

  const handleBillStatusConfirm = async (status: BillStatus) => {
    if (!billStatusOrderId) return
    await dispatch(
      doUpdateOrder({
        id: billStatusOrderId,
        dto: { billStatus: status },
      }),
    )
    setBillStatusOrderId(null)
  }

  const handleDmpClick = (
    order: OrderDto,
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    dispatch(setSelectedOrder(order))
    setDialogAction('Dmp')
  }

  const handleValiderClose = () => {
    setValiderAnchorEl(null)
    setValiderOrder(null)
  }

  // Legacy handlers removed: QuickFseActions now drives the full flow

  const actions = useMemo(() => {
    return (
      actionsMap[filters.status ?? OrderStatusEnum.pendingRegistration] || []
    )
  }, [filters.status])

  const filteredOrders = useMemo(() => {
    if (excludeRows) {
      return orders.filter((o) => !excludeRows.includes(o.id))
    }
    return orders
  }, [orders, excludeRows])

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'visit',
        headerName: 'N° de séjour',
        width: 120,
        sortable: false,
        renderCell: ({ row }) => {
          return <span className="text-sm">{row.visit?.identifier}</span>
        },
      },
      {
        field: 'patient.firstName',
        headerName: 'Prénom',
        width: 120,
        renderCell: ({ row }) => {
          return (
            <span className="text-sm">{capitalize(row.patient.firstName)}</span>
          )
        },
      },
      {
        field: 'patient.lastName',
        headerName: 'Nom',
        width: 120,
        renderCell: ({ row }) => {
          return (
            <span className="text-sm">{capitalize(row.patient.lastName)}</span>
          )
        },
      },
      {
        field: 'patient.birthDate',
        headerName: 'Né(e) le',
        width: 100,
        renderCell: ({ row }) => {
          return <span className="text-sm">{row.patient.birthDateStr}</span>
        },
      },
      {
        field: 'patient.ssn',
        headerName: 'N° sécurité sociale',
        width: 160,
        sortable: false,
        renderCell: ({ row }) => {
          return <span className="text-sm">{row.patient.ssn}</span>
        },
      },

      {
        field: 'exam.label',
        headerName: 'Procédure',
        width: 150,
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <span className="text-xs">
              {row.medicalOrders?.map((mo) => mo.exam.code).join('; ')}
            </span>
          )
        },
      },

      {
        field: 'plannedDate',
        headerName: 'RDV',
        width: 120,
        renderCell: (params) => {
          return (
            <div className="text-xs">
              {moment(params.row.plannedDate).format('DD/MM/YYYY HH:mm')}
            </div>
          )
        },
      },
      {
        field: 'attendingDoctor',
        headerName: 'Praticien',
        sortable: false,
        width: 120,
        renderCell: ({ row }) => {
          if (row.attendingDoctor?.firstName && row.attendingDoctor?.lastName) {
            const firstName = row.attendingDoctor.firstName
              .charAt(0)
              .toUpperCase()
            const lastName = row.attendingDoctor.lastName.toUpperCase()
            return (
              <span className="text-xs">
                {firstName}. {lastName}
              </span>
            )
          }
          return 'Non renseigné'
        },
      },

      {
        field: 'aldStatus',
        headerName: 'ALD',
        sortable: false,
        width: 40,
        renderCell: ({ row }) => {
          return (
            <div
              className="cursor-pointer"
              onClick={() => handleALDStatusClick(row)}
            >
              <CustomALDIcon aldStatus={row.patient.PatientAld} />
            </div>
          )
        },
      },
      {
        field: 'couvertureStatus',
        renderHeader: () => (
          <Tooltip title="Droits">
            <ShieldCheck className="w-[20px]" />
          </Tooltip>
        ),
        width: 40,
        sortable: false,
        renderCell: ({ row }) => {
          const coverageId = row.visit?.coverageId

          return (
            <div
              className="cursor-pointer"
              onClick={() => handleCouvertureClick(row)}
            >
              <CustomCouvertureIcon coverageId={coverageId} />
            </div>
          )
        },
      },
      {
        field: 'memberComments',
        sortable: false,
        renderHeader: () => (
          <Tooltip title="Commentaires">
            <MessageCircle size={20} />
          </Tooltip>
        ),
        width: 40,
        renderCell: (params: GridRenderCellParams<string, OrderDto>) => {
          if (!params.row) {
            return null
          }

          return (
            <CommentsPopoverTrigger
              size="small"
              order={params.row}
              handleOpenComments={handleOpenComments}
            />
          )
        },
      },
      {
        field: 'Indications',
        headerName: 'Indications',
        width: 140,
        renderCell: (params: GridRenderCellParams<string, OrderDto>) => {
          return (
            <div className="flex items-center gap-2">
              {params.row.isEmergency && (
                <Tooltip title="Urgent">
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="red"
                    className="cursor-default px-1 border border-red-500 rounded"
                  >
                    U
                  </Typography>
                </Tooltip>
              )}
              {params.row.isNightShift && (
                <Tooltip title="Nuit">
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="purple"
                    className="cursor-default px-1 border border-purple-500 rounded"
                  >
                    N
                  </Typography>
                </Tooltip>
              )}
              {params.row.isHoliday && (
                <Tooltip title="Dimanche / J. férié">
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="orange"
                    className="cursor-default px-1 border border-orange-500 rounded"
                  >
                    F
                  </Typography>
                </Tooltip>
              )}
              {params.row.astreinte && (
                <Tooltip title="Astreinte">
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="green"
                    className="cursor-default px-1 border border-green-500 rounded"
                  >
                    A
                  </Typography>
                </Tooltip>
              )}
            </div>
          )
        },
      },
      {
        field: 'patientTransport',
        renderHeader: () => <CarTaxiFront className="w-[20px]" />,
        width: 120,
        sortable: false,
        renderCell: (params) => {
          return (
            <div className="flex items-center justify-end">
              {params.row.patientTransport
                ? params.row.patientTransport.status ===
                  PatientTransportStatus.Called
                  ? `Appelé à ${moment(
                      params.row.patientTransport.calledAt,
                    ).format('HH:mm')}`
                  : 'Non appelé'
                : ''}
              {}
            </div>
          )
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 380,
        sortable: false,
        hide: isSelection,
        renderCell: (params) => {
          const medicalOrder = params.row
          return (
            <div className="flex space-x-2">
              {actionBtnObj.reduce((acc, el) => {
                if (actions.includes(el.actionKey)) {
                  const unpaidCount =
                    el.actionKey === 'unpaidInvoices'
                      ? unpaidCountByPatientId.get(medicalOrder.patient.id) || 0
                      : 0

                  if (el.actionKey === 'unpaidInvoices' && unpaidCount === 0) {
                    return acc
                  }

                  if (
                    el.actionKey === 'quickPay' &&
                    !orderToInvoiceMap.has(medicalOrder.id)
                  ) {
                    return acc
                  }

                  const isPatientRegistered =
                    filters.status !== OrderStatusEnum.pendingRegistration
                  const isRegisterPatientAction =
                    el.actionKey === 'registerPatient'

                  acc.push(
                    <Tooltip
                      key={medicalOrder.id + '-' + el.btnName}
                      title={
                        isRegisterPatientAction && isPatientRegistered
                          ? 'Patient déjà enregistré'
                          : el.btnName
                      }
                    >
                      <div
                        className={`p-1 rounded relative ${
                          isRegisterPatientAction && isPatientRegistered
                            ? 'cursor-default opacity-60'
                            : 'cursor-pointer hover:bg-gray-200'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (isRegisterPatientAction && isPatientRegistered) {
                            return
                          }
                          if (el.actionKey === 'report') {
                            handleReportClick(medicalOrder, e)
                          } else if (el.actionKey === 'Dmp') {
                            handleDmpClick(medicalOrder, e)
                          } else if (el.actionKey === 'validerFse') {
                            const actionFn = el.action?.bind({})
                            actionFn && actionFn(medicalOrder, e)
                          } else if (el.action) {
                            const actionFn = el.action.bind({})
                            actionFn(medicalOrder, e)
                          }
                        }}
                      >
                        {isRegisterPatientAction && isPatientRegistered ? (
                          <div className="relative">
                            <MeetingRoomIcon className="text-gray-400" />
                            <CheckIcon className="text-green-500 absolute -top-1 -right-1 w-3 h-3" />
                          </div>
                        ) : (
                          el.icon
                        )}
                        {el.actionKey === 'unpaidInvoices' &&
                          unpaidCount > 0 && (
                            <div className="absolute top-0 right-0 bg-red-500/80 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                              {unpaidCount}
                            </div>
                          )}
                        {el.actionKey === 'documents' &&
                          (() => {
                            const count =
                              medicalOrder.documents != null
                                ? medicalOrder.documents.length
                                : medicalOrder.documentsCount ?? 0
                            return count > 0 ? (
                              <div className="absolute bottom-1 right-1 bg-red-500 text-white text-[10px] rounded-full size-[14px] flex items-center justify-center">
                                {count}
                              </div>
                            ) : null
                          })()}
                      </div>
                    </Tooltip>,
                  )
                }
                return acc
              }, new Array<React.ReactElement>())}
            </div>
          )
        },
      },
    ],
    [
      actions,
      actionBtnObj,
      unpaidCountByPatientId,
      orderToInvoiceMap,
      handleOpenComments,
      filters.status,
      isSelection,
    ],
  )

  useEffect(() => {
    if (!filters.status && !filters.hasRadiologyStudy) return

    setIsLoading(true)
    dispatch(doFindOrders())
      .unwrap()
      .then((res) => {
        const ids = res.datas.map((o) => o.id)
        if (ids.length > 0) {
          dispatch(doGetOrderTransports(ids))
          dispatch(getOrdersWithUnpaidInvoices({ orderIds: ids }))
        }
        const patientIds = Array.from(
          new Set(res.datas.map((o) => o.patient?.id).filter(Boolean)),
        ) as number[]
        if (patientIds.length > 0) {
          dispatch(findPatientsDebts({ patientIds }))
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [
    filters.status,
    filters.hasRadiologyStudy,
    filters.endDate,
    filters.startDate,
    filters.search,
    filters.siteId,
    filters.patientIds,
    filters.limit,
    filters.page,
    dispatch,
  ])

  // useEffect(() => {
  //   const orderIds = orders.map((o) => o.id)
  //   dispatch(doGetDmpByOrders(orderIds))
  // }, [orders])

  const reportWindow = useReportWindow({
    onReportCreated: () => {
      dispatch(doFindOrders())
      enqueueSnackbar('Rapport créé avec succès', { variant: 'success' })
    },
    onShellClosed() {},
    onShowUI(data) {
      if (data.name === 'unpaid-bills') {
        setUnpaidOpen(true)
        setUnpaidPatientId(data.args.patientId)
      }
    },
  })

  const handleSelectReport = async (url: string) => {
    setAnchorEl(null)
    setDialogAction(null)

    // Check if there are multiple displays
    let hasExternalDisplay = false

    try {
      // Modern approach: Screen Detection API
      if ('isExtended' in window.screen) {
        hasExternalDisplay = (window.screen as any).isExtended
      }
    } catch (error) {
      console.log('Screen detection not available, using fallback')
    }

    if (hasExternalDisplay) {
      // Open in popup window for multi-display setup
      reportWindow.openReport(url)
    } else {
      // Show in dialog for single display
      setSelectedReport(url)
    }
  }

  const onRefresh = useCallback(() => {
    setIsLoading(true)
    dispatch(doFindOrders()).finally(() => {
      setIsLoading(false)
    })
  }, [dispatch])

  const _onDeleteConfirm = () => {
    setIsDeleteDialogOpen(false)
  }

  const handleDialogClose = () => {
    setDialogAction(null)
    dispatch(setSelectedOrder(null))
    if (urlState?.orderId) {
      updateParam('orderId', null)
    }
  }

  const handleOrderClick = (order: OrderDto) => {
    updateParam('orderId', order.id)
    dispatch(setSelectedOrder(order))
    setDialogAction('MedicalOrder')
  }

  const handleDocumentsClick = (order: OrderDto) => {
    dispatch(setSelectedOrder(order))
    setDialogAction('Documents')
    setIsDocumentsLoading(true)
    dispatch(doGetOrderDocuments(order.id)).finally(() => {
      setIsDocumentsLoading(false)
    })
  }

  const handleEventLogsClick = (order: OrderDto) => {
    dispatch(setSelectedOrder(order))
    setDialogAction('eventLogs')
  }

  const handleSelectionChange = (ids: number[]) => {
    const _orders: OrderDto[] = []
    for (const id of ids) {
      const order = orders.find((o) => o.id === id)
      if (order) {
        _orders.push(order)
      }
    }
    if (onSelectionChange) {
      onSelectionChange(_orders)
    }
  }

  const handlePatientRegistered = () => {
    handleMedicalOrderView()
  }

  // const selectedOrderDmps = useMemo(() => {
  //   return dmpList.filter((d) =>
  //     selectedOrder?.medicalOrders.map((m) => m.id).includes(d.medicalOrderId),
  //   )
  // }, [dmpList, selectedOrder])

  const handleOnFileUploaded = (doc: Document) => {
    if (!selectedOrder) {
      return
    }
    setisEditingDocuments(false)
    dispatch(
      updateOrderDocuments({
        orderId: selectedOrder.id,
        documents: [...(selectedOrder.documents ?? []), doc],
      }),
    )
  }

  const handleDocumentDeleted = (doc: Document) => {
    if (!selectedOrder) {
      return
    }
    dispatch(
      updateOrderDocuments({
        orderId: selectedOrder.id,
        documents: (selectedOrder.documents ?? []).filter(
          (d) => d.id !== doc.id,
        ),
      }),
    )
  }

  return (
    <div>
      <Grid
        style={{
          height: 'calc(100vh - 130px)',
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        <DataGridPro
          density="standard"
          style={{
            height: '100%',
            width: '100%',
            padding: '12px',
            backgroundColor: 'white',
          }}
          sx={{
            opacity: isLoading ? 0.5 : 1,
            pointerEvents: isLoading ? 'none' : 'auto',
            transition: 'opacity 0.15s ease-in-out',
          }}
          getRowClassName={(params) => {
            if (params.row.patientTransport?.status === 'NOT_CALLED') {
              return '!bg-yellow-100'
            }
            if (params.row.patientTransport?.status === 'CALLED') {
              return '!bg-green-100'
            }
            return ''
          }}
          checkboxSelection={isSelection}
          onSelectionModelChange={(selected) => {
            handleSelectionChange(selected as number[])
          }}
          columns={columns}
          rows={isLoading ? [] : filteredOrders}
          rowCount={filters.total}
          disableSelectionOnClick
          disableColumnMenu
          pagination
          paginationMode="server"
          disableMultipleSelection
          onPageChange={(page) => {
            dispatch(setOrderFilters({ page }))
          }}
          initialState={{
            pinnedColumns: { left: ['actions'], right: [''] },
          }}
          loading={isLoading}
          components={{
            Toolbar: OrderToolbar,
            LoadingOverlay: LinearProgress,
          }}
          componentsProps={{
            toolbar: {
              onRefresh,
              onInstantBillClick: () => setIsInstantBillOpen(true),
              urlState: urlState,
              updateParam: updateParam,
              updateParams: updateParams,
              clearParams: clearParams,
            },
          }}
        />
        <CommentsPopover
          createdByFullName={user?.firstName + ' ' + user?.lastName}
          anchorEl={commentsPopover.anchorEl}
          onClose={handleCloseComments}
          order={commentsPopover.order}
          setOpenCommentDialog={setCommentDialogOpen}
          setEditingComment={setEditingComment}
          editingComment={editingComment}
          commentDialogOpen={commentDialogOpen}
          handleCloseComments={handleCloseComments}
        />
      </Grid>
      {/* add dialog title with close button */}

      <RegisterPatientForm
        isOpen={dialogAction === 'PatientForm' && !!selectedOrder}
        onClose={handleDialogClose}
        onRegistered={handlePatientRegistered}
      />
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        maxWidth="lg"
        aria-labelledby="form-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 5,
            background: theme.palette.primary.main,
          },
        }}
      >
        <DialogTitle
          onClose={() => setIsDeleteDialogOpen(false)}
          title="deleteReception"
          style={{
            background: '#fff',
            color: theme.palette.primary.main,
          }}
          format
        />
        <DialogContent
          sx={{
            background: '#E2E6F2',
            minWidth: '250px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Text
            text="deleteConfirmation"
            variant="h5"
            color="secondary"
            sx={{ fontWeight: 'bold', paddingTop: '20px' }}
            format
          />
        </DialogContent>
        <DialogActions
          sx={{
            background: '#fff',
          }}
        >
          <Button
            fullWidth
            textColor="white"
            onClick={(e) => {
              e.stopPropagation()
              setIsDeleteDialogOpen(false)
            }}
            type="button"
            color="secondary"
            text="Cancel"
          />
          <Button
            fullWidth
            textColor="white"
            onClick={(e) => {
              e.stopPropagation()
              _onDeleteConfirm()
            }}
            color="primary"
            text="Save"
          />
        </DialogActions>
      </Dialog>
      {dialogAction === 'patientFusion' && selectedOrder && (
        <PatientFusionDialog
          patientId={selectedOrder.patient.id}
          open
          onClose={handleDialogClose}
        />
      )}
      {dialogAction === 'ALDStatus' && selectedOrder && (
        <Dialog
          open={true}
          onClose={handleDialogClose}
          fullWidth={true}
          maxWidth="lg"
          aria-labelledby="form-dialog-title2"
          PaperProps={{
            sx: {
              borderRadius: 5,
            },
          }}
        >
          <DialogTitle
            onClose={() => {
              setDialogAction(null)
            }}
            title="Gestion de l'ALD"
            style={{
              background: '#fff',
            }}
          />
          <DialogContent sx={{ bgcolor: 'white', minHeight: '500px' }}>
            <PatientAldList patientId={selectedOrder.patient.id} />
          </DialogContent>
        </Dialog>
      )}

      {selectedOrder && dialogAction === 'MedicalOrder' && (
        <OrderDialog
          isOpen={dialogAction === 'MedicalOrder'}
          onClose={handleDialogClose}
          order={selectedOrder}
        />
      )}
      <SlideInPortal
        isOpen={!!selectedOrder && dialogAction === 'Couverture'}
        onClose={() => {
          setDialogAction(null)
          setSelectedOrder(null)
          dispatch(setPatientDetails(null))
        }}
        patientId={selectedOrder?.patient.id}
        visitId={selectedOrder?.visitId}
      />

      {isInstantBillOpen && (
        <InstantBillComponent
          isOpen={isInstantBillOpen}
          onClose={() => setIsInstantBillOpen(false)}
        />
      )}

      {/* Unpaid invoices modal */}
      <Dialog
        open={unpaidOpen}
        onClose={() => setUnpaidOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          title={`Factures impayées${
            unpaidPatientName ? ` — ${unpaidPatientName}` : ''
          }`}
          onClose={() => setUnpaidOpen(false)}
          format
        />
        <DialogContent sx={{ pt: 1 }}>
          {unpaidPatientId != null && (
            <UnpaidPatientInvoices patientId={unpaidPatientId} />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setUnpaidOpen(false)}
            text="Fermer"
          />
        </DialogActions>
      </Dialog>

      {validerOrder && (
        <QuickFseActions
          order={validerOrder}
          anchorEl={validerAnchorEl}
          open={!!validerAnchorEl}
          onClose={handleValiderClose}
        />
      )}

      {/* Quick Pay: Direct payment form for the invoice */}
      <Dialog
        open={!!paymentInvoice}
        onClose={handlePaymentDialogClose}
        TransitionComponent={UpTransition}
        fullScreen
      >
        <DialogContent sx={{ p: 0 }}>
          {paymentInvoice && (
            <PatientPaymentForm
              paymentCase={TransactionType.PAYMENT}
              invoiceToPay={paymentInvoice}
              onClose={handlePaymentDialogClose}
              onPaymentSuccess={() => {
                const patientId = paymentInvoice?.patient?.id
                if (patientId) {
                  dispatch(findPatientsDebts({ patientIds: [patientId] }))
                }
                if (paymentInvoice?.orderId) {
                  dispatch(
                    getOrdersWithUnpaidInvoices({
                      orderIds: [paymentInvoice.orderId],
                    }),
                  )
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Pay for Unpaid Invoices: Show patient invoices filtered to TO_BE_PAID for this patient */}
      <UnpaidPatientInvoicesDialog
        quickPayOpen={quickPayOpen}
        setQuickPayOpen={setQuickPayOpen}
        quickPayPatientId={quickPayPatientId}
        onPaymentComplete={(orderId) => {
          if (!orderId) {
            return
          }
          setBillStatusOrderId(orderId)
          setShowBillStatusDialog(true)
        }}
      />

      {selectedOrder && dialogAction === 'Transport' && (
        <Dialog
          open={true}
          onClose={() => setDialogAction(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 5,
            },
          }}
        >
          <DialogTitle
            title="Transport"
            onClose={() => setDialogAction(null)}
            format
          />
          <DialogContent>
            <TransportForm row={selectedOrder} isOrder />
          </DialogContent>
        </Dialog>
      )}
      {selectedOrder && dialogAction === 'Report' && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null)
            setDialogAction(null)
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, minWidth: '300px' }}>
            <Typography variant="h6" gutterBottom>
              Compte rendu
            </Typography>
            <ReportSelection
              order={selectedOrder}
              onSelectReport={handleSelectReport}
            />
          </Box>
        </Popover>
      )}
      {/* {dialogAction === 'Dmp' && selectedOrder && (
        <Dialog
          open={true}
          onClose={() => setDialogAction(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 5,
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <DialogTitle
            title="DMP"
            onClose={() => setDialogAction(null)}
            format
          />
          <DialogContent>
            <CreateDmpComponent
              order={selectedOrder}
              dmps={selectedOrderDmps}
            />
          </DialogContent>
        </Dialog>
      )} */}

      {selectedOrder && dialogAction === 'Documents' && (
        <Dialog
          open={true}
          onClose={() => setDialogAction(null)}
          maxWidth="sm"
          fullWidth
          aria-labelledby="form-dialog-title"
        >
          <Typography variant="h6" px={2} pt={2}>
            Fichiers attachés
          </Typography>
          <DialogContent>
            {selectedOrder && (
              <DocumentsList
                entityId={selectedOrder.id}
                entityName={EntityNameEnum.ORDER}
                documents={selectedOrder.documents ?? []}
                setisEditingDocuments={setisEditingDocuments}
                handleOnFileUploaded={handleOnFileUploaded}
                isEditingDocuments={isEditingDocuments}
                onDocumentDeleted={handleDocumentDeleted}
                setSelectedDocument={setSelectedDocument}
                isLoading={isDocumentsLoading}
              />
            )}
          </DialogContent>
          <DialogActions>
            <MuiButton
              variant="outlined"
              size="small"
              onClick={() => setDialogAction(null)}
            >
              Annuler
            </MuiButton>
          </DialogActions>
        </Dialog>
      )}

      {selectedOrder && (
        <DocumentListViewer
          isOpen={selectedDocument !== null}
          documents={selectedOrder.documents ?? []}
          onClose={() => {
            setSelectedDocument(null)
          }}
          selectedDocument={selectedDocument || undefined}
          onSelectedDocumentChange={(doc) => {
            setSelectedDocument(doc)
          }}
        />
      )}

      {selectedOrder && dialogAction === 'eventLogs' && (
        <Dialog
          open={true}
          onClose={() => setDialogAction(null)}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent>
            <EventLogsOrderTable
              entityId={selectedOrder.id.toString()}
              entityName={EntityNameEnum.ORDER}
            />
          </DialogContent>
          <DialogActions>
            <MuiButton
              variant="outlined"
              size="small"
              onClick={() => setDialogAction(null)}
            >
              Fermer
            </MuiButton>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={!!selectedReport}
        onClose={() => setSelectedReport(null)}
        fullScreen
        TransitionComponent={UpTransition}
        sx={{ zIndex: 10001 }}
        PaperProps={{
          sx: {
            borderRadius: 0,
            p: 0,
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 16, zIndex: 10 }}
            onClick={() => setSelectedReport(null)}
          >
            <CloseIcon sx={{ color: 'white', fontSize: '28px' }} />
          </IconButton>
          {!!selectedReport && (
            <iframe
              src={selectedReport}
              style={{ width: '100%', height: '100%' }}
              allow="microphone"
              onLoad={() => registerIframe(window)}
            />
          )}
        </DialogContent>
      </Dialog>
      <BillStatusDialog
        open={showBillStatusDialog}
        onClose={() => {
          setShowBillStatusDialog(false)
          setBillStatusOrderId(null)
        }}
        onConfirm={handleBillStatusConfirm}
      />
    </div>
  )
}
